import { appPath } from '../lib/basePath';
/**
 * Dropdown behavior thresholds — module-level singleton.
 *
 * 0.36.278 — Two platform-wide knobs that control whether
 * HenaketDropdown / HenaketMultiDropdown render as a panel, inline
 * radio/checkbox group, or panel-with-search:
 *
 *   inlineThreshold — when `options.length <= inlineThreshold`, the
 *     dropdown short-circuits the panel entirely and renders a
 *     FormRadioGroup (single-select) or FormCheckboxGroup
 *     (multi-select). Default 7. Set to 0 to disable the
 *     transformation.
 *
 *   searchThreshold — when `options.length > searchThreshold`, the
 *     dropdown shows the search field at the top of the panel.
 *     Default 15.
 *
 * Hydrated once at app mount from /api/public/settings (see App.jsx)
 * — the same place we already hydrate date/time format. Components
 * read via getDropdownThresholds() synchronously at render time;
 * defaults kick in before the fetch resolves so the first render
 * isn't blocked.
 *
 * 0.36.278 follow-up — Admin-context override: inside the admin
 * panel (`/admin/*` routes), the inline threshold is FORCED to 1.
 * That effectively means "always show as a dropdown" for every
 * practical case (no real-world admin picker has only one option),
 * which matches admin's need for compact pickers in filter
 * toolbars, settings rows, table headers, and so on. The platform
 * threshold continues to apply to the citizen-facing area
 * (everything outside /admin/). See getEffectiveInlineThreshold()
 * for the path check.
 *
 * Why a module singleton instead of React context: HenaketDropdown is
 * mounted from dozens of admin pages and forms, often deep inside
 * already-rendered trees. Threading a context provider everywhere
 * would be invasive; reading from a module is zero-cost. The trade-
 * off is that settings changes mid-session don't propagate until
 * the next reload, which matches how the other public settings
 * (date format, time format) behave.
 */

let _inlineThreshold = 7;
let _searchThreshold = 15;

// 0.36.278 — Admin-context inline threshold. Inside /admin/* every
// dropdown stays a dropdown (no auto-expansion to radio/checkbox)
// so admin filter toolbars, settings rows, and table pickers keep
// their compact UI. Search behavior in admin matches citizen
// behavior — same platform threshold applies in both areas.
const ADMIN_INLINE_THRESHOLD = 0;

export function setDropdownThresholds({ inlineThreshold, searchThreshold }) {
  if (Number.isFinite(inlineThreshold) && inlineThreshold >= 0) {
    _inlineThreshold = Math.floor(inlineThreshold);
  }
  if (Number.isFinite(searchThreshold) && searchThreshold >= 0) {
    _searchThreshold = Math.floor(searchThreshold);
  }
}

export function getDropdownThresholds() {
  return {
    inlineThreshold: _inlineThreshold,
    searchThreshold: _searchThreshold,
  };
}

// Raw platform-setting accessors. The search accessor has no admin
// override — admin uses the same platform value as the citizen area.
export function getInlineThreshold() { return _inlineThreshold; }
export function getSearchThreshold() { return _searchThreshold; }

/**
 * 0.36.278 — Effective inline threshold for the current render.
 *
 * Returns 0 (auto-expansion disabled) when the URL is under /admin/,
 * otherwise the platform setting. Read at every dropdown render so
 * a single SPA navigation between admin and citizen-facing routes
 * gets the right behavior without a reload.
 *
 * SSR-safe: returns the platform value when `window` is undefined.
 */
function _isAdminRoute() {
  if (typeof window === 'undefined') return false;
  const path = window.location && appPath();
  return typeof path === 'string' && path.startsWith('/admin/');
}

export function getEffectiveInlineThreshold() {
  return _isAdminRoute() ? ADMIN_INLINE_THRESHOLD : _inlineThreshold;
}
